import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as leaderboardService from '@/services/leaderboard.service';
import type {
  LeaderboardEntry,
  LeaderboardFilters,
  LeaderboardResponse,
  UserLeaderboardPosition,
} from '@/services/leaderboard.service';
import { ApiError } from '@/services/api';

// State interface
interface LeaderboardState {
  entries: LeaderboardEntry[];
  myPosition: UserLeaderboardPosition | null;
  filters: LeaderboardFilters;
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
  isLoading: boolean;
  isLoadingMore: boolean;
  isLoadingPosition: boolean;
  error: string | null;
}

// Initial state
const initialState: LeaderboardState = {
  entries: [],
  myPosition: null,
  filters: {
    category: 'overall',
    timeframe: 'all',
    limit: 50,
    offset: 0,
  },
  pagination: {
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false,
  },
  isLoading: false,
  isLoadingMore: false,
  isLoadingPosition: false,
  error: null,
};

// Thunks
export const fetchLeaderboard = createAsyncThunk<
  LeaderboardResponse,
  LeaderboardFilters | undefined,
  { rejectValue: string }
>('leaderboard/fetch', async (filters, { rejectWithValue }) => {
  try {
    return await leaderboardService.getLeaderboard(filters);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden des Leaderboards');
  }
});

export const loadMoreLeaderboard = createAsyncThunk<
  LeaderboardResponse,
  void,
  { rejectValue: string; state: { leaderboard: LeaderboardState } }
>('leaderboard/loadMore', async (_, { rejectWithValue, getState }) => {
  try {
    const { leaderboard } = getState();
    const newOffset = leaderboard.pagination.offset + leaderboard.pagination.limit;
    return await leaderboardService.getLeaderboard({
      ...leaderboard.filters,
      offset: newOffset,
    });
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden weiterer Eintraege');
  }
});

export const fetchMyPosition = createAsyncThunk<
  UserLeaderboardPosition | null,
  void,
  { rejectValue: string }
>('leaderboard/fetchMyPosition', async (_, { rejectWithValue }) => {
  try {
    return await leaderboardService.getMyPosition();
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden deiner Position');
  }
});

export const fetchUserPosition = createAsyncThunk<
  UserLeaderboardPosition | null,
  string,
  { rejectValue: string }
>('leaderboard/fetchUserPosition', async (userId, { rejectWithValue }) => {
  try {
    return await leaderboardService.getUserPosition(userId);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Position');
  }
});

// Slice
const leaderboardSlice = createSlice({
  name: 'leaderboard',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<LeaderboardFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    clearLeaderboard: (state) => {
      state.entries = [];
      state.myPosition = null;
      state.pagination = initialState.pagination;
    },
  },
  extraReducers: (builder) => {
    // Fetch leaderboard
    builder
      .addCase(fetchLeaderboard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLeaderboard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.entries = action.payload.entries;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchLeaderboard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Load more
    builder
      .addCase(loadMoreLeaderboard.pending, (state) => {
        state.isLoadingMore = true;
        state.error = null;
      })
      .addCase(loadMoreLeaderboard.fulfilled, (state, action) => {
        state.isLoadingMore = false;
        state.entries = [...state.entries, ...action.payload.entries];
        state.pagination = action.payload.pagination;
      })
      .addCase(loadMoreLeaderboard.rejected, (state, action) => {
        state.isLoadingMore = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Fetch my position
    builder
      .addCase(fetchMyPosition.pending, (state) => {
        state.isLoadingPosition = true;
        state.error = null;
      })
      .addCase(fetchMyPosition.fulfilled, (state, action) => {
        state.isLoadingPosition = false;
        state.myPosition = action.payload;
      })
      .addCase(fetchMyPosition.rejected, (state, action) => {
        state.isLoadingPosition = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });

    // Fetch user position
    builder
      .addCase(fetchUserPosition.pending, (state) => {
        state.isLoadingPosition = true;
        state.error = null;
      })
      .addCase(fetchUserPosition.fulfilled, (state, action) => {
        state.isLoadingPosition = false;
        // Store in myPosition for display purposes
        state.myPosition = action.payload;
      })
      .addCase(fetchUserPosition.rejected, (state, action) => {
        state.isLoadingPosition = false;
        state.error = action.payload || 'Unbekannter Fehler';
      });
  },
});

export const { setFilters, clearError, clearLeaderboard } = leaderboardSlice.actions;

export default leaderboardSlice.reducer;
